import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  ActivityLogService,
  type ActivityLog,
} from "@/services/activityLogService";
import { TbSearch, TbRefresh } from "react-icons/tb";

interface ActivityLogsProps {
  scope: "tenant" | "platform";
  tenantId?: string;
}

const ACTION_COLORS: Record<string, string> = {
  create: "bg-green-100 text-green-800",
  update: "bg-blue-100 text-blue-800",
  delete: "bg-red-100 text-red-800",
  invite: "bg-purple-100 text-purple-800",
  login: "bg-yellow-100 text-yellow-800",
  logout: "bg-gray-100 text-gray-800",
};

const getActionColor = (action: string): string => {
  const key = Object.keys(ACTION_COLORS).find((k) =>
    action.toLowerCase().includes(k)
  );
  return key ? ACTION_COLORS[key] : "bg-gray-100 text-gray-800";
};

const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const getUserName = (log: ActivityLog): string => {
  if (log.user) {
    const name = [log.user.first_name, log.user.last_name]
      .filter(Boolean)
      .join(" ");
    return name || log.user.email;
  }
  return "System";
};

export const ActivityLogs: React.FC<ActivityLogsProps> = ({
  scope,
  tenantId,
}) => {
  const [searchTerm, setSearchTerm] = useState("");

  const {
    data: logs = [],
    isLoading,
    error,
    refetch,
  } = useQuery<ActivityLog[]>({
    queryKey: ["activity-logs", scope, tenantId],
    queryFn: () =>
      scope === "tenant" && tenantId
        ? ActivityLogService.getTenantLogs(tenantId)
        : ActivityLogService.getPlatformLogs(),
    enabled: scope === "platform" || !!tenantId,
    staleTime: 15000,
  });

  const filteredLogs = useMemo(() => {
    if (!searchTerm) return logs;
    const term = searchTerm.toLowerCase();
    return logs.filter(
      (log) =>
        log.action.toLowerCase().includes(term) ||
        log.entity_type.toLowerCase().includes(term) ||
        getUserName(log).toLowerCase().includes(term) ||
        JSON.stringify(log.details).toLowerCase().includes(term)
    );
  }, [logs, searchTerm]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-[#828282] text-[14px]">Loading logs...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
        Failed to load activity logs. Please try again.
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 w-full">
      {/* Header */}
      <div className="flex items-center justify-between gap-4">
        <div>
          <h2 className="text-[#444444] text-[18px] font-bold">
            Activity Logs
          </h2>
          <p className="text-[#828282] text-[13px] mt-1">
            {scope === "tenant"
              ? "Recent activity within this tenant"
              : "Platform-wide activity"}
          </p>
        </div>
        <button
          onClick={() => refetch()}
          className="cursor-pointer flex items-center gap-2 px-3 py-2 text-[13px] font-medium text-[#444] bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
        >
          <TbRefresh size={16} />
          Refresh
        </button>
      </div>

      {/* Search */}
      <div className="flex p-[13px_14px] items-center gap-4 w-full rounded-[10px] bg-[#F3F4F6]">
        <div className="flex justify-center items-center gap-2 flex-1">
          <TbSearch className="w-4 h-4 text-[#828282]" />
          <input
            type="text"
            placeholder="Search logs..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="bg-transparent border-none outline-none text-[#828282] font-['Open_Sans'] text-[12px] font-medium leading-[18px] flex-1 placeholder:text-[#828282]"
          />
        </div>
      </div>

      {/* Log entries */}
      {filteredLogs.length === 0 ? (
        <div className="text-center py-12">
          <div className="text-[#828282] text-[14px]">
            {searchTerm
              ? "No logs match your search."
              : "No activity logs yet."}
          </div>
        </div>
      ) : (
        <div className="space-y-2">
          {filteredLogs.map((log) => (
            <div
              key={log.id}
              className="bg-white rounded-xl p-4 border border-gray-100 hover:border-gray-200 transition-colors"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1.5">
                    <span
                      className={`px-2.5 py-0.5 rounded-full text-[11px] font-semibold ${getActionColor(log.action)}`}
                    >
                      {log.action}
                    </span>
                    <span className="text-[12px] text-[#828282] font-medium">
                      {log.entity_type}
                    </span>
                  </div>
                  <p className="text-[13px] text-[#444444]">
                    <span className="font-semibold">{getUserName(log)}</span>
                    {log.entity_id && (
                      <span className="text-[#828282]">
                        {" "}
                        &middot; ID: {log.entity_id.slice(0, 8)}...
                      </span>
                    )}
                  </p>
                  {log.details && Object.keys(log.details).length > 0 && (
                    <p className="text-[12px] text-[#828282] mt-1 truncate">
                      {Object.entries(log.details)
                        .map(([k, v]) => `${k}: ${v}`)
                        .join(", ")}
                    </p>
                  )}
                </div>
                <span className="text-[11px] text-[#828282] whitespace-nowrap shrink-0">
                  {formatDate(log.created_at)}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
